"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.transformReceivable = exports.getRequiredFields = exports.getAdvanceRateFields = exports.getConcentrationLimitFields = void 0;
const common_1 = require("@finley-technologies/common");
const formula_engine_1 = require("@finley-technologies/formula-engine");
const calcAgentMechanics_1 = require("../../calcAgentMechanics");
const getFilterFields = (filter) => {
    if (filter.operation === 'orderBySum') {
        return [filter.field, filter.groupBy];
    }
    return [filter.field];
};
const getAllFilterFields = (filters) => {
    const fields = [];
    filters.forEach((filter) => fields.push(...getFilterFields(filter)));
    return fields;
};
const getConcentrationLimitFields = (resolvedCalcAgentMechanics) => {
    const requiredFields = new Set([
        'concentrationLimitFields.eligibleExposureLessConcentrationLimitKickouts',
        'requiredIntermediateFields.waiveAllConcentration',
        'requiredIntermediateFields.includeInConcentrationLimitDenominatorIfWaived',
        'eligibilityFields.eligibleExposure',
        'valuationFields.valuation',
    ]);
    // concentration limit fields
    const concentrationLimits = (0, calcAgentMechanics_1.flattenConcentrationLimits)(resolvedCalcAgentMechanics);
    concentrationLimits.forEach((limit) => {
        // Check all waiver config formulas
        // Waiver configs do not support excel engine, so no range variables will be present
        limit.waiveConfigs.forEach((waiveConfig) => {
            // Get fields from waiver configs
            const { nonRangeVariables } = (0, formula_engine_1.getFormulaComposition)(waiveConfig.config.formula);
            Array.from(nonRangeVariables).forEach((variable) => requiredFields.add(variable));
        });
        // Check all calculation config fields
        switch (limit.calculationConfig.type) {
            case 'standard':
                // Only filter fields
                getAllFilterFields(limit.calculationConfig.config.filters).forEach((field) => requiredFields.add(field));
                break;
            case 'weightedAverage':
                // Specified field + filter fields
                requiredFields.add(limit.calculationConfig.config.field);
                getAllFilterFields(limit.calculationConfig.config.filters).forEach((field) => requiredFields.add(field));
                break;
            case 'exposureByGroup':
                // Grouping field + filter fields
                requiredFields.add(limit.calculationConfig.config.groupingField);
                getAllFilterFields(limit.calculationConfig.config.filters).forEach((field) => requiredFields.add(field));
                break;
            case 'directInsert':
                // Direct insert fields
                requiredFields.add(limit.calculationConfig.config.kickoutValueField);
                getAllFilterFields(limit.calculationConfig.config.filters).forEach((field) => requiredFields.add(field));
                break;
            case 'resolvedPointer':
                (0, common_1.finleyAssert)(limit.calculationConfig.config.requiredFields, 'Custom concentration limits must specify required fields for streaming pipeline usage.');
                limit.calculationConfig.config.requiredFields.forEach((field) => requiredFields.add(field));
                getAllFilterFields(limit.calculationConfig.config.filters ?? []).forEach((field) => requiredFields.add(field));
                break;
            default:
                (0, common_1.finleyAssertUnreachable)(limit.calculationConfig);
        }
    });
    return requiredFields;
};
exports.getConcentrationLimitFields = getConcentrationLimitFields;
const getAdvanceRateFields = (resolvedCalcAgentMechanics) => {
    const requiredFields = new Set([
        'concentrationLimitFields.eligibleExposureLessConcentrationLimitKickouts',
    ]);
    // Advance rate fields
    resolvedCalcAgentMechanics.loanProducts.forEach((lp) => {
        if (lp.receivableAdvanceRateConfig.type === 'formula') {
            const { nonRangeVariables } = (0, formula_engine_1.getFormulaComposition)(lp.receivableAdvanceRateConfig.config.formula);
            Array.from(nonRangeVariables).forEach((variable) => requiredFields.add(variable));
        }
    });
    // TODO: @sachinr4 to review advance rate tree formulas (not required for initial launch)
    return requiredFields;
};
exports.getAdvanceRateFields = getAdvanceRateFields;
// Helper method to avoid calling both concentration and advance rate separately
const getRequiredFields = (resolvedCalcAgentMechanics) => {
    const requiredFields = new Set([
        ...(0, exports.getConcentrationLimitFields)(resolvedCalcAgentMechanics),
        ...(0, exports.getAdvanceRateFields)(resolvedCalcAgentMechanics),
    ]);
    return Array.from(requiredFields);
};
exports.getRequiredFields = getRequiredFields;
// transformReceivable
// What does this do?
// - It accepts a list of required fields for usage within both concentration limit and advance rate evaluation
// - It returns an output data structure consisting of only the required fields, while maintaining the same structure as the ConcentrationLimitReceivable type to ensure compatibility with existing formulas
// - The way this function operates can be changed depending on future callers (if keys can be flattened to just `key.field` format, the same way that formula engine operates)
//
// Why do we need it?
// - This is necessary to minimize the memory footprint of the receivable object being used within concentration limit calculations.
// - With a correctly sized worker, it can be handled without this adjustment, but memory will grow linearly with the number of receivables and can lead to unnecessary runtime failures as a result of memory allocation issues
// - After this adjustment, even a tape with 5 million receivables can be handled in a local development environment (benchmarked for this specific case around a max of 7GB of memory)
const transformReceivable = (receivable, requiredFields) => {
    const transformed = {};
    let useConcentrationLimitReasons = false;
    let useConcentrationLimitWaivers = false;
    requiredFields.forEach((requiredField) => {
        const [field, subField, ...other] = requiredField.split('.');
        // TODO: @sachinr4 to add new typeguard to capture all potential top level properties
        const propertyKey = field;
        const propertyField = subField;
        // concentrationLimitReasons and concentrationLimitWaivers are array properties that may be
        // referenced with deep nesting (e.g. "concentrationLimitWaivers.maxMonthlyAdvanceLimit.maxMonthly")
        // by formulas. These are handled separately below — skip the per-field transform loop.
        if (propertyKey === 'concentrationLimitReasons') {
            useConcentrationLimitReasons = true;
            return;
        }
        if (propertyKey === 'concentrationLimitWaivers') {
            useConcentrationLimitWaivers = true;
            return;
        }
        (0, common_1.finleyAssert)(other.length === 0, 'Only 1 level of nesting is allowed for receivable data attributes');
        // TODO: @sachinr4 to review type casting in the field mapping below
        // Nested property
        if (subField) {
            const receivableProperty = receivable[propertyKey];
            // Guaranteed to be some kind of object or null (for assignment fields)
            (0, common_1.finleyAssert)(typeof receivableProperty === 'object', `Receivable attribute ${propertyKey} did not contain valid data for use within calculation agent`);
            if (receivableProperty === null) {
                transformed[propertyKey] = null;
            }
            else if (Array.isArray(receivableProperty)) {
                transformed[propertyKey] = receivableProperty;
            }
            else {
                // This branch handles the nested property case where it is neither null (assignmentFields) nor an array (eligibilityStatuses, concentrationLimitReasons, concentrationLimitWaivers)
                // Given that this is an `object` type as asserted above, the only remaining option here is a value corresponding to some non-empty object {}
                // The workflow here is:
                // 1) Retrieve the current state of the `transformed` variable or initialize with an empty object {}
                // 2) Assert again for "not array" to satisfy the compiler (the compiler is right because `transformed` is a different object from `receivable`, but in practice the way we use it, it will not be different)
                // 3) Update the requested property field in the `current` object
                // 4) Set the `transformed` object field value to the `current` object
                // Set new object if not already available
                const current = transformed[propertyKey] ?? {};
                (0, common_1.finleyAssert)(!Array.isArray(current), 'not array');
                current[propertyField] =
                    receivableProperty[propertyField];
                transformed[propertyKey] = current;
            }
        }
        else {
            // Top level property
            transformed[propertyKey] = receivable[propertyKey];
        }
    });
    // Ensure the following are always set:
    transformed.finleyReceivableId = receivable.finleyReceivableId;
    transformed.loanProductId = receivable.loanProductId;
    // If a formula references concentrationLimitReasons/concentrationLimitWaivers (e.g. advance rate
    // formulas that read waiver values), copy the full arrays from the input receivable so the data
    // is available after concentration limit evaluation populates them.
    // Otherwise, initialize as empty arrays so the evaluation process can fill them in.
    transformed.concentrationLimitReasons = useConcentrationLimitReasons
        ? receivable.concentrationLimitReasons
        : [];
    transformed.concentrationLimitWaivers = useConcentrationLimitWaivers
        ? receivable.concentrationLimitWaivers
        : [];
    return transformed;
};
exports.transformReceivable = transformReceivable;
//# sourceMappingURL=transform.js.map